/**
 * Verifica se o ambiente atual é desenvolvimento
 * Em produção e staging, retorna false
 */
export const isDevelopment = (): boolean => {
    if (typeof window === 'undefined') return false;

    const env = process.env.NODE_ENV;
    const hostname = window.location.hostname;

    // Considera desenvolvimento apenas se:
    // 1. NODE_ENV é 'development' OU
    // 2. Hostname é localhost ou 127.0.0.1
    return (
        env === 'development' ||
        hostname === 'localhost' ||
        hostname === '127.0.0.1' ||
        hostname.includes('.local')
    );
};

/**
 * Carrega dados do localStorage apenas em desenvolvimento
 * Em produção/staging, retorna null
 */
export const getLocalStorageInDev = (key: string): string | null => {
    if (!isDevelopment()) {
        return null;
    }

    if (typeof window === 'undefined') return null;

    try {
        return localStorage.getItem(key);
    } catch (error) {
        console.error(`Erro ao acessar localStorage para chave "${key}":`, error);
        return null;
    }
};

/**
 * Salva dados no localStorage (permitido em todos os ambientes)
 * Usado para persistir dados durante a sessão do usuário
 */
export const setLocalStorage = (key: string, value: string): void => {
    if (typeof window === 'undefined') return;

    try {
        localStorage.setItem(key, value);
    } catch (error) {
        console.error(`Erro ao salvar no localStorage para chave "${key}":`, error);
    }
};

/**
 * Remove dados do localStorage
 */
export const removeLocalStorage = (key: string): void => {
    if (typeof window === 'undefined') return;

    try {
        localStorage.removeItem(key);
    } catch (error) {
        console.error(`Erro ao remover do localStorage para chave "${key}":`, error);
    }
};

/**
 * Chave do localStorage para armazenar o último CNPJ usado
 */
const LAST_USED_CNPJ_KEY = 'lastUsedCnpj';

/**
 * Lista de todas as chaves do localStorage relacionadas ao fluxo de agendamento
 */
const SCHEDULING_STORAGE_KEYS = [
    'companyData',
    'empresaData',
    'employeesData',
    'examsData',
    'orderData',
    'stepData',
    'pedidoId',
    'solicitanteId',
];

/**
 * Limpa todos os dados do localStorage relacionados ao fluxo de agendamento
 */
export const clearAllSchedulingData = (): void => {
    if (typeof window === 'undefined') return;

    console.log('🧹 [clearAllSchedulingData] Limpando todos os dados do fluxo de agendamento');

    try {
        SCHEDULING_STORAGE_KEYS.forEach((key) => {
            localStorage.removeItem(key);
        });
        console.log('✅ [clearAllSchedulingData] Todos os dados foram removidos');
    } catch (error) {
        console.error('❌ [clearAllSchedulingData] Erro ao limpar localStorage:', error);
    }
};

/**
 * Recupera o último CNPJ usado do localStorage
 * @returns CNPJ sem formatação ou null se não existir
 */
export const getLastUsedCnpj = (): string | null => {
    if (typeof window === 'undefined') return null;

    try {
        return localStorage.getItem(LAST_USED_CNPJ_KEY);
    } catch (error) {
        console.error('❌ [getLastUsedCnpj] Erro ao recuperar último CNPJ:', error);
        return null;
    }
};

/**
 * Salva o CNPJ atual no localStorage
 * @param cnpj - CNPJ ou CPF sem formatação
 */
export const setLastUsedCnpj = (cnpj: string): void => {
    if (typeof window === 'undefined') return;

    try {
        // Remove formatação antes de salvar
        const cleanedCnpj = cnpj.replace(/\D/g, '');
        localStorage.setItem(LAST_USED_CNPJ_KEY, cleanedCnpj);
        console.log(`💾 [setLastUsedCnpj] CNPJ salvo: ${cleanedCnpj}`);
    } catch (error) {
        console.error('❌ [setLastUsedCnpj] Erro ao salvar CNPJ:', error);
    }
};

/**
 * Verifica se o CNPJ mudou e limpa o localStorage se necessário
 * @param cnpj - CNPJ ou CPF (pode ter formatação)
 */
export const clearIfCnpjChanged = (cnpj: string): void => {
    if (typeof window === 'undefined') return;

    // Remove formatação para comparação
    const cleanedCnpj = cnpj.replace(/\D/g, '');
    const lastCnpj = getLastUsedCnpj();

    // Se não tem último CNPJ ou é diferente, limpar tudo
    if (!lastCnpj || lastCnpj !== cleanedCnpj) {
        if (lastCnpj) {
            console.log(`🔄 [clearIfCnpjChanged] CNPJ mudou de ${lastCnpj} para ${cleanedCnpj} - limpando localStorage`);
        } else {
            console.log(`🔄 [clearIfCnpjChanged] Primeiro CNPJ inserido (${cleanedCnpj}) - limpando localStorage`);
        }
        
        clearAllSchedulingData();
        setLastUsedCnpj(cleanedCnpj);
    } else {
        console.log(`✅ [clearIfCnpjChanged] CNPJ não mudou (${cleanedCnpj}) - mantendo dados`);
    }
};

/**
 * Limpa APENAS dados relacionados ao pedido, colaboradores e agendamento
 * Preserva dados da empresa (companyData, empresaData) e outras configurações
 */
export const clearPedidoAndColaboradoresData = (): void => {
    if (typeof window === 'undefined') return;

    console.log('🧹 [clearPedidoAndColaboradoresData] Limpando dados de pedido, colaboradores e agendamento');

    const keysToRemove = [
        'pedidoId',
        'pedidoData',
        'orderData',
        'pixData',
        'pixError',
        'employeesData',
        'examsData',
        'stepData',
    ];

    try {
        keysToRemove.forEach((key) => {
            localStorage.removeItem(key);
        });
        console.log('✅ [clearPedidoAndColaboradoresData] Dados de pedido, colaboradores e agendamento removidos');
    } catch (error) {
        console.error('❌ [clearPedidoAndColaboradoresData] Erro ao limpar localStorage:', error);
    }
};
